import React from 'react'
import {
  Button,
  InfoFilledIcon,
  NotificationBanner as TangramNotificationBanner,
  Text,
} from '@ntuctech/tangram-web'
import styled from 'styled-components'
import { SCREEN_SIZE } from '../../../lib/Media'
import { trackFindSimilarButtonClickEvent } from '../../../lib/analytics/events/buttonClickEvents/FindSimilarClickedEvent'

export const PRODUCT_STATUS = {
  OUT_OF_STOCK: 'out_of_stock',
  UNAVAILABLE_FOR_DELIVERY: 'unavailable_for_delivery',
  AVAILABLE: 'available',
}

const STORE_STATUS = {
  ENABLED: 'ENABLED',
}

const NOTIFICATION_MESSAGES = {
  [PRODUCT_STATUS.OUT_OF_STOCK]: 'Out of stock online',
  [PRODUCT_STATUS.UNAVAILABLE_FOR_DELIVERY]: postalCode =>
    `Unavailable for delivery to ${postalCode}`,
}

export const getFindSimilarSectionConfig = (
  storeStatus,
  itemIsOutOfStock,
  postalCode
) => {
  const productStatus = getProductStatus(storeStatus, itemIsOutOfStock)
  const description = getNotificationMessage(productStatus, postalCode)

  return {
    productStatus,
    description,
  }
}

export const getNotificationMessage = (
  productStatus,
  postalCode = 'your location'
) => {
  const message = NOTIFICATION_MESSAGES[productStatus]

  return typeof message === 'function' ? message(postalCode) : message
}

export const getProductStatus = (storeStatus, itemIsOutOfStock) => {
  if (storeStatus !== STORE_STATUS.ENABLED) {
    return PRODUCT_STATUS.UNAVAILABLE_FOR_DELIVERY
  } else if (itemIsOutOfStock) {
    return PRODUCT_STATUS.OUT_OF_STOCK
  } else {
    return PRODUCT_STATUS.AVAILABLE
  }
}

const ProductAvailabilityMessage = () => {
  return (
    <Text size="s">Buy this in-store or find similar items for delivery.</Text>
  )
}

const NotificationBanner = ({ description }) => {
  return (
    <TangramNotificationBanner variant="information" icon={InfoFilledIcon}>
      <TangramNotificationBanner.Content>
        <TangramNotificationBanner.Description>
          {description}
        </TangramNotificationBanner.Description>
      </TangramNotificationBanner.Content>
    </TangramNotificationBanner>
  )
}

const FindSimilarButton = ({ width, onClick: scrollToSimilarProducts }) => {
  const handleOnclick = event => {
    event.currentTarget.blur()
    trackFindSimilarButtonClickEvent()
    scrollToSimilarProducts()
  }

  return (
    <Button
      size="m"
      variant="secondary"
      onClick={handleOnclick}
      style={{ width }}
    >
      Find similar
    </Button>
  )
}

const StyledFindSimilarWebSection = styled.div`
  .notification-banner-wrapper {
    width: 20.625rem;
    margin-top: 1.5rem;
  }

  .product-availability-message-wrapper {
    margin-top: 0.5rem;
  }

  ${SCREEN_SIZE.Below.Mobile} {
    display: none;
  }
`

const StyledFindSimilarActionBar = styled.div`
  display: none;
  position: fixed;
  z-index: 1000;
  width: 100%;
  bottom: 0;
  margin-bottom: 0 !important;

  .find-similar-button-wrapper {
    padding: 1rem;
    background-color: white;
  }

  ${SCREEN_SIZE.Below.Mobile} {
    display: block;
  }
`
const ProductAvailabilityMessageWrapper = styled.div`
  display: none;
  background-color: white;
  margin: 1rem 0 !important;
  padding: 1rem;

  ${SCREEN_SIZE.Below.Mobile} {
    display: block;
  }
`

export const FindSimilarWebSection = ({
  description,
  productStatus,
  onClick,
}) => {
  return (
    <StyledFindSimilarWebSection>
      <FindSimilarButton width="13.5rem" onClick={onClick} />
      {description && (
        <div className="notification-banner-wrapper">
          <NotificationBanner description={description} />
        </div>
      )}
      {productStatus === PRODUCT_STATUS.UNAVAILABLE_FOR_DELIVERY && (
        <div className="product-availability-message-wrapper">
          <ProductAvailabilityMessage />
        </div>
      )}
    </StyledFindSimilarWebSection>
  )
}

export const FindSimilarActionBar = ({ description, onClick }) => {
  return (
    <StyledFindSimilarActionBar>
      {description && <NotificationBanner description={description} />}
      <div className="find-similar-button-wrapper">
        <FindSimilarButton width="100%" onClick={onClick} />
      </div>
    </StyledFindSimilarActionBar>
  )
}

const FindSimilarSection = ({
  layout,
  description,
  productStatus,
  scrollToSimilarProducts,
}) => {
  return (
    <>
      {layout === 'web' && (
        <FindSimilarWebSection
          description={description}
          productStatus={productStatus}
          onClick={scrollToSimilarProducts}
        />
      )}
      {layout === 'mobile' && (
        <>
          {productStatus === PRODUCT_STATUS.UNAVAILABLE_FOR_DELIVERY && (
            <ProductAvailabilityMessageWrapper>
              <ProductAvailabilityMessage />
            </ProductAvailabilityMessageWrapper>
          )}
          <FindSimilarActionBar
            description={description}
            onClick={scrollToSimilarProducts}
          />
        </>
      )}
    </>
  )
}

export default FindSimilarSection
